const HerbalDrugTechUnit5Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <!-- 1. Chapter Header -->
    <div>
        <h2 class="text-2xl font-black text-green-900 mb-3 border-b-2 border-slate-200 pb-2">Unit V: Herbal Industry & Schedule T (GMP)</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            This final unit covers the commercial landscape of the herbal drug industry, key research institutions in India, and the stringent quality standards mandated under Schedule T (GMP) for manufacturing Ayurvedic, Siddha, and Unani (ASU) medicines.
        </p>
    </div>

    <!-- 2. General Introduction to Herbal Industry -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-green-800 mb-4 border-b border-slate-100 pb-2">1. General Introduction to Herbal Industry</h3>
        
        <div class="mb-5">
            <h4 class="text-[15px] font-bold text-green-900 mb-2">Present Scope & Future Prospects</h4>
            <p class="text-[13px] text-slate-700 mb-3 leading-relaxed text-justify">
                Globally, there is a massive paradigm shift from synthetic chemicals toward natural products due to their lower side effects, biodegradability, and holistic healing approach. The global herbal medicine market is growing exponentially, offering huge scope in sectors like nutraceuticals, phytopharmaceuticals, and herbal cosmetics.
            </p>
        </div>

        <!-- Industries & Institutions -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div class="border border-slate-200 p-4 rounded-lg bg-slate-50">
                <h4 class="text-[15px] font-bold text-green-700 mb-2">Major Plant-Based Industries in India</h4>
                <ul class="list-disc pl-5 space-y-1 text-[12px] text-slate-600">
                    <li><strong>Dabur India Ltd.</strong> (World's largest Ayurvedic medicine manufacturer).</li>
                    <li><strong>The Himalaya Drug Company</strong> (Pioneered pharmaceutical-grade herbal formulations).</li>
                    <li><strong>Baidyanath</strong> (Shree Baidyanath Ayurved Bhawan, traditional formulations).</li>
                    <li><strong>Patanjali Ayurved</strong> (Brought hyper-growth and mass commercialization).</li>
                </ul>
            </div>

            <div class="border border-slate-200 p-4 rounded-lg bg-slate-50">
                <h4 class="text-[15px] font-bold text-green-700 mb-2">Key Research Institutions in India</h4>
                <ul class="list-disc pl-5 space-y-1 text-[12px] text-slate-600">
                    <li><strong>CIMAP</strong> (Central Institute of Medicinal and Aromatic Plants, Lucknow) - Focuses on agricultural practices and breeding of MAPs.</li>
                    <li><strong>NBRI</strong> (National Botanical Research Institute, Lucknow) - Focuses on taxonomy, conservation, and botanical research.</li>
                    <li><strong>CDRI</strong> (Central Drug Research Institute, Lucknow) - Involved in active drug discovery and clinical development of phytochemical molecules.</li>
                    <li><strong>IIIM</strong> (Indian Institute of Integrative Medicine, Jammu) - Explores natural products for drug development.</li>
                </ul>
            </div>
        </div>
    </div>

    <!-- 3. Schedule T - Good Manufacturing Practice (GMP) -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-green-800 mb-4 border-b border-slate-100 pb-2">2. Schedule T: GMP for ASU Medicines</h3>
        <p class="text-[13px] text-slate-700 mb-3">
            <strong>Schedule T</strong> of the Drugs and Cosmetics Rules, 1945, prescribes the <strong>Good Manufacturing Practices (GMP)</strong> for factories manufacturing Ayurvedic, Siddha, and Unani (ASU) drugs.
        </p>
        
        <!-- Objectives -->
        <div class="bg-green-50 p-4 border border-green-200 rounded-lg mb-5 text-[13px] text-slate-700">
            <p class="font-bold text-green-900 mb-1">Core Objectives of Schedule T:</p>
            <ul class="list-disc pl-5 space-y-1">
                <li>To ensure that raw materials used are authenticated and free from contamination.</li>
                <li>To maintain strict hygienic conditions during manufacturing, processing, and packaging.</li>
                <li>To establish standard operating procedures (SOPs) to ensure batch-to-batch batch uniformity.</li>
                <li>To maintain proper documentation and records for tracing defects or recalls.</li>
            </ul>
        </div>

        <!-- Components of GMP -->
        <h4 class="text-[16px] font-bold text-green-900 mb-3">Key Components and Requirements</h4>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-[13px] text-slate-700">
            
            <div class="p-3 border border-slate-100 bg-slate-50 rounded">
                <p class="font-bold text-green-800 mb-1">🛒 Working Space & Infrastructure</p>
                <p class="text-[12px] text-slate-600">The factory building must be designed to permit a unidirectional flow of work. It must prevent cross-contamination and be free from insects, rodents, or dust. Separate areas must be allotted for receiving raw material, garbling, processing, packaging, and quality control.</p>
            </div>

            <div class="p-3 border border-slate-100 bg-slate-50 rounded">
                <p class="font-bold text-green-800 mb-1">📦 Storage Area</p>
                <p class="text-[12px] text-slate-600">Must be dry, well-ventilated, and protected from direct sunlight. It requires distinct sections: Raw Materials, Quarantined Materials (awaiting lab clearance), Rejected Materials, and Finished Products.</p>
            </div>

            <div class="p-3 border border-slate-100 bg-slate-50 rounded">
                <p class="font-bold text-green-800 mb-1">⚙️ Machinery & Equipment</p>
                <p class="text-[12px] text-slate-600">All machinery (disintegrators, mixers, coating pans, tableting machines) must be constructed from non-corrosive materials (typically Stainless Steel 304 or 316) to avoid metal contamination. They must be cleaned thoroughly before switching batches.</p>
            </div>

            <div class="p-3 border border-slate-100 bg-slate-50 rounded">
                <p class="font-bold text-green-800 mb-1">🧼 Health, Hygiene & Sanitation</p>
                <p class="text-[12px] text-slate-600">Workers must undergo regular medical checkups. They must wear clean protective clothing, gloves, headgear, and masks. Changing rooms, handwashing stations, and clean toilets must be located outside the processing area.</p>
            </div>

            <div class="p-3 border border-slate-100 bg-slate-50 rounded md:col-span-2">
                <p class="font-bold text-green-800 mb-1">📝 SOPs, Documentation & Records</p>
                <p class="text-[12px] text-slate-600 font-medium">Standard Operating Procedures (SOPs) must be laid down for every industrial action. Crucial documents that must be securely stored for at least <strong>1 year beyond the expiry date</strong> of the batch include:</p>
                <ul class="list-disc pl-5 mt-1 text-[12px] text-slate-600 font-normal space-y-1">
                    <li><strong>Batch Manufacturing Records (BMR):</strong> Complete history of the production steps of each batch.</li>
                    <li><strong>Raw Material Register:</strong> Records of source, quantity, batch no., and quality analysis.</li>
                    <li><strong>Distribution Record:</strong> Detailed logs of where the finished products were sold (essential for recalls).</li>
                </ul>
            </div>

        </div>
    </div>

</div>
`;

export default HerbalDrugTechUnit5Notes;